import type { App } from 'vue';
import { reactive } from 'vue';
import { loadScript } from 'vue-plugin-load-script';

import api from '@/api/index.ts';
import axios from '@/plugins/axios.js';
import { notify } from '@/plugins/vue-notification.ts';
import i18n from '@/plugins/vue-i18n.js';
import behavior from '@/plugins/mixpanel.ts';

declare const ZiggeoApi: any;

interface ZiggeoVideo {
    id: number;
    ziggeoId: string;
    status: string;
    progress: number;
    embedding: any;
    node: HTMLElement;
    processingStartedAt: number | null;
    processingDelayed: boolean;
    processingToken: string;
    source: VideoSource | null;
}

type VideoSource = 'recorded' | 'uploaded';

type ProcessingContext = Pick<
    ZiggeoVideo,
    'processingStartedAt' | 'processingDelayed' | 'processingToken' | 'source'
>;

interface PendingDeletion {
    token: string;
    processed: boolean;
    generation: number;
}

type RecorderEventHandler = (...args: unknown[]) => void;

interface ZiggeoRecorder {
    _videoFile: { type: string };
    get: (key: string) => string;
    off?: (event: string, handler: RecorderEventHandler) => void;
    on: (event: string, handler: RecorderEventHandler) => void;
    reset: () => void;
}

interface RecorderLifecycle {
    recorder: ZiggeoRecorder;
    generation: number;
    handlers: Map<string, RecorderEventHandler>;
}

const API_KEY = process.env.VUE_APP_ZIGGEO_API_KEY;
const PROCESSING_DELAY_MS = 60_000;
const ZIGGEO_SDK_REVISION = 'r38';

const fingerprintMediaToken = (token: string): string => {
    let hash = 7;

    for (const character of token) {
        hash = (hash * 31 + (character.codePointAt(0) || 0)) % 2147483647;
    }

    return hash.toString(36).padStart(6, '0');
};

const setZiggeoLocale = () => {
    ZiggeoApi.V2.Locale.mediaLocale.register(
        {
            'ba-videorecorder-chooser.record-video': i18n.t(
                'communications.video.ziggeoTranslations.record'
            ),
            'ba-videorecorder-chooser.upload-video': i18n.t(
                'communications.video.ziggeoTranslations.upload'
            ),
        },
        [`language:${i18n.getContentLanguage()}`],
        10
    );

    ZiggeoApi.V2.Locale.setLocale(i18n.getContentLanguage() || 'en');
};

class Ziggeo {
    ziggeoApp: any = null; // eslint-disable-line @typescript-eslint/no-explicit-any

    data = reactive<{ videos: ZiggeoVideo[] }>({ videos: [] });

    private readonly processingTimers = new Map<number, ReturnType<typeof setTimeout>>();

    private readonly beforeUnloadHandlers = new Map<number, (event: BeforeUnloadEvent) => string>();

    private readonly pendingDeletions = new Map<string, PendingDeletion>();

    private readonly deletionPromises = new Map<string, Promise<boolean>>();

    private readonly lifecycleGenerations = new Map<number, number>();

    private readonly recorderLifecycles = new Map<number, RecorderLifecycle>();

    async init(): Promise<void> {
        if (!API_KEY || this.ziggeoApp) {
            return;
        }

        if (!window.ZiggeoApi) {
            await loadScript('https://assets.ziggeo.com/v2-r38/ziggeo.js');
        }

        if (!window.ZiggeoApi) {
            return;
        }

        this.ziggeoApp = new window.ZiggeoApi.V2.Application({
            token: API_KEY,
            webrtc_on_mobile: true,
            auth: true,
        });
    }

    getVideoFile(videoId = ''): string {
        return `https://embed-cdn.ziggeo.com/v1/applications/${API_KEY}/videos/${videoId}/video.mp4`;
    }

    async getVideoStateFromZiggeo(videoId = '') {
        await this.init();

        if (!this.ziggeoApp) {
            return null;
        }

        return api.videos.videoState(videoId);
    }

    async fetchVideoState(
        id: number,
        video: Partial<ZiggeoVideo>,
        resetOnRequestError = true
    ): Promise<void> {
        if (!video.ziggeoId) {
            return;
        }

        const requestedToken = video.ziggeoId;
        const requestedGeneration = this.getLifecycleGeneration(id);

        await this.init();

        if (!window.ZiggeoApi) {
            return;
        }

        const sameZiggeoIdVideo = this.getSameZiggeoIdVideo(video as ZiggeoVideo);

        if (sameZiggeoIdVideo) {
            return;
        }

        const response = await this.getVideoStateFromZiggeo(requestedToken);

        if (!response) {
            return;
        }

        response.success((data: { state_string: string }) => {
            if (!this.isLifecycleGenerationCurrent(id, requestedGeneration)) {
                return;
            }

            const currentVideo = this.getVideoById(id);

            if (currentVideo?.ziggeoId !== requestedToken) {
                return;
            }

            if (currentVideo.processingToken && currentVideo.processingToken !== requestedToken) {
                return;
            }

            switch (data.state_string) {
                case 'READY':
                    if (currentVideo.processingToken) {
                        this.completeProcessing(id, requestedToken);
                        return;
                    }

                    this.updateVideo(id, { status: 'processed', ziggeoId: requestedToken });
                    break;

                case 'FAILED':
                case 'EMPTY':
                    if (currentVideo.processingToken) {
                        this.failProcessing(id, requestedToken);
                        return;
                    }

                    this.updateVideo(id, { status: '', ziggeoId: '' });
                    break;

                default:
                    this.updateVideo(id, {
                        status: 'uploaded',
                        ziggeoId: requestedToken,
                    });
            }
        });

        response.error(() => {
            if (!this.isLifecycleGenerationCurrent(id, requestedGeneration)) {
                return;
            }

            if (this.getVideoById(id)?.ziggeoId !== requestedToken) {
                return;
            }

            if (resetOnRequestError) {
                this.resetVideo(id);
            }
        });
    }

    mountRecorder(id: number, element: HTMLElement): void {
        const video = this.getVideoById(id);

        if (!video?.embedding) {
            return;
        }

        this.registerRecorderLifecycle(id, video.embedding);
        video.embedding.reset();

        element.appendChild(video.node);

        this.fetchVideoState(id, video);
    }

    async createRecorder(
        id: number,
        element: HTMLElement,
        ziggeoId: string,
        user: { account: { sid: string }; id: number }
    ): Promise<void> {
        if (!element) {
            return;
        }

        const getVideo = this.getVideoById(id);

        if (
            getVideo &&
            !['', 'sent', 'readyToRecord', 'recordingStopped'].includes(getVideo.status)
        ) {
            if (['uploading', 'uploaded'].includes(getVideo.status) && getVideo.embedding) {
                this.registerRecorderLifecycle(id, getVideo.embedding);
                this.resumeProcessingTimer(id);

                try {
                    await this.fetchVideoState(id, getVideo, false);
                } catch {
                    // The recorder's processed event remains the primary completion signal.
                } finally {
                    this.resumeProcessingTimer(id);
                }
            }

            return;
        }

        await this.init();

        if (!window.ZiggeoApi) {
            return;
        }

        setZiggeoLocale();

        const root = document.createElement('div');

        if (element && !element.querySelector('.ba-videorecorder-container')) {
            element.appendChild(root);
        }

        const recorder = new ZiggeoApi.V2.Recorder({
            element: root,
            attrs: {
                'video-profile': '_activix',
                recordingwidth: '1080',
                recordingheight: '720',
                fittodimensions: true,
                allowcustomupload: false,
                theme: 'modern',
                'custom-data': {
                    account_sid: user.account.sid,
                    user_id: user.id,
                    lead_id: id,
                },
            },
        });

        const video: Partial<ZiggeoVideo> = {
            ziggeoId: ziggeoId || '',
            status: '',
            progress: 0,
            embedding: recorder,
            node: root,
            processingStartedAt: null,
            processingDelayed: false,
            processingToken: '',
            source: null,
        };

        if (this.getVideoById(id)) {
            this.clearVideoLifecycle(id);
            this.updateVideo(id, video);
        } else {
            this.setVideo(id, video);
        }

        this.registerRecorderLifecycle(id, recorder);
        recorder.activate();

        this.fetchVideoState(id, video);
    }

    updateVideo(id: number, payload: Partial<ZiggeoVideo>): void {
        const index = this.data.videos.findIndex((video) => video.id === id);

        if (index === -1) {
            return;
        }

        this.data.videos.splice(index, 1, {
            ...this.data.videos[index],
            id,
            ...payload,
        });
    }

    getSameZiggeoIdVideo(video: ZiggeoVideo): ZiggeoVideo | undefined {
        return this.data.videos.find((recorder) => {
            return video.id !== recorder.id && video.ziggeoId === recorder.ziggeoId;
        });
    }

    getVideoById(id: number): ZiggeoVideo | null {
        return this.data.videos.find((video) => video.id === id) || null;
    }

    resetVideo(id: number): void {
        const video = this.getVideoById(id);

        if (!video) {
            return;
        }

        if (video.embedding) {
            video.embedding.reset();
        }

        this.resetVideoZiggeo(id);
    }

    resetVideoZiggeo(id: number): void {
        const index = this.data.videos.findIndex((video) => video.id === id);

        if (index === -1) {
            return;
        }

        this.clearVideoLifecycle(id);
        this.clearPendingDeletions(id);

        this.data.videos.splice(index, 1, {
            id,
            status: '',
            ziggeoId: '',
            progress: 0,
            embedding: this.data.videos[index]?.embedding,
            node: this.data.videos[index].node,
            processingStartedAt: null,
            processingDelayed: false,
            processingToken: '',
            source: null,
        });
    }

    setVideo(id: number, payload: Partial<ZiggeoVideo>): void {
        if (this.data.videos.some((video) => video.id === id)) {
            return;
        }

        this.data.videos.push({
            id,
            ziggeoId: '',
            status: '',
            progress: 0,
            embedding: null,
            node: document.createElement('div'),
            processingStartedAt: null,
            processingDelayed: false,
            processingToken: '',
            source: null,
            ...payload,
        });
    }

    deleteVideo(id: number, ziggeoId: string | null = null): Promise<boolean> {
        const deletionToken = ziggeoId || this.getVideoById(id)?.ziggeoId || '';
        const deletionKey = this.getDeletionKey(id, deletionToken);
        const pendingDeletion = this.deletionPromises.get(deletionKey);

        if (pendingDeletion) {
            return pendingDeletion;
        }

        const deletion = this.performDeleteVideo(id, deletionToken, deletionKey);
        this.deletionPromises.set(deletionKey, deletion);

        deletion
            .finally(() => {
                if (this.deletionPromises.get(deletionKey) === deletion) {
                    this.deletionPromises.delete(deletionKey);
                }
            })
            .catch(() => undefined);

        return deletion;
    }

    clearVideoLifecycle(id: number): void {
        this.clearProcessingTimer(id);
        this.removeBeforeUnloadHandler(id);
        this.disposeRecorderLifecycle(id);
        this.lifecycleGenerations.set(id, this.getLifecycleGeneration(id) + 1);
    }

    clearProcessingTimer(id: number): void {
        const timer = this.processingTimers.get(id);

        if (timer) {
            clearTimeout(timer);
            this.processingTimers.delete(id);
        }
    }

    private async performDeleteVideo(
        id: number,
        deletionToken: string,
        deletionKey: string
    ): Promise<boolean> {
        const processingVideo = this.getVideoById(id);
        const deletionGeneration = this.getLifecycleGeneration(id);
        const processingContext: ProcessingContext | null = processingVideo
            ? {
                  processingStartedAt: processingVideo.processingStartedAt,
                  processingDelayed: processingVideo.processingDelayed,
                  processingToken: processingVideo.processingToken,
                  source: processingVideo.source,
              }
            : null;
        const beforeUnloadHandler = this.beforeUnloadHandlers.get(id);

        if (
            processingContext?.processingToken &&
            processingContext.processingToken === deletionToken
        ) {
            this.pendingDeletions.set(deletionKey, {
                token: processingContext.processingToken,
                processed: false,
                generation: deletionGeneration,
            });
        }

        this.clearProcessingTimer(id);
        this.removeBeforeUnloadHandler(id);

        try {
            await this.init();

            if (!this.ziggeoApp) {
                this.restoreAfterDeletionFailure(
                    id,
                    deletionKey,
                    deletionGeneration,
                    processingContext,
                    beforeUnloadHandler
                );

                return false;
            }

            if (deletionToken) {
                behavior.track('VideoPresenting', { action: 'deleted' });
                await api.videos.destroy(deletionToken);
                notify.info(i18n.t('communications.video.isDeleted'));
            }

            if (processingContext?.processingDelayed && processingContext.processingToken) {
                this.trackProcessingOutcome(
                    'processing-cancelled',
                    processingContext.processingStartedAt,
                    processingContext.processingToken,
                    processingContext.source,
                    true
                );
            }

            this.pendingDeletions.delete(deletionKey);

            if (this.videoMatchesToken(id, deletionToken)) {
                this.resetVideoZiggeo(id);
            }

            return true;
        } catch (error) {
            this.restoreAfterDeletionFailure(
                id,
                deletionKey,
                deletionGeneration,
                processingContext,
                beforeUnloadHandler
            );

            throw error;
        }
    }

    private registerRecorderLifecycle(id: number, recorder: ZiggeoRecorder): void {
        this.clearVideoLifecycle(id);

        const generation = this.getLifecycleGeneration(id);
        const handlers = new Map<string, RecorderEventHandler>();
        const isActive = () => {
            const lifecycle = this.recorderLifecycles.get(id);

            return lifecycle?.generation === generation && lifecycle.recorder === recorder;
        };
        const onUnload = (event: BeforeUnloadEvent) => {
            const confirmationMessage = 'Your video is still uploading';

            event.preventDefault();
            return confirmationMessage;
        };
        const addHandler = (event: string, handler: RecorderEventHandler) => {
            const guardedHandler: RecorderEventHandler = (...args) => {
                if (isActive()) {
                    handler(...args);
                }
            };

            handlers.set(event, guardedHandler);
            recorder.on(event, guardedHandler);
        };

        this.recorderLifecycles.set(id, { recorder, generation, handlers });

        addHandler('uploading', () => {
            behavior.track('VideoPresenting', { action: 'uploading' });
            this.updateVideo(id, {
                status: 'uploading',
                ziggeoId: recorder.get('video'),
            });

            this.removeBeforeUnloadHandler(id);
            this.beforeUnloadHandlers.set(id, onUnload);
            window.addEventListener('beforeunload', onUnload);
        });

        addHandler('upload_progress', (kbSent, kbTotal) => {
            if (typeof kbSent !== 'number' || typeof kbTotal !== 'number') {
                return;
            }

            this.updateVideo(id, { progress: Math.round((kbSent / kbTotal) * 100) });
        });

        addHandler('ready_to_record', () => {
            behavior.track('VideoPresenting', { action: 'recording-ready' });
            this.updateVideo(id, { status: 'readyToRecord' });
        });

        addHandler('recording_stopped', () => {
            behavior.track('VideoPresenting', { action: 'recording-stopped' });
            this.updateVideo(id, { status: 'recordingStopped', source: 'recorded' });
        });

        addHandler('uploaded', () => {
            const token = recorder.get('video');
            const source = this.getVideoById(id)?.source || 'recorded';

            this.startProcessing(id, token, source);
        });

        addHandler('processed', () => {
            const token = this.getVideoById(id)?.processingToken || recorder.get('video');

            if (!this.completeProcessing(id, token)) {
                return;
            }

            notify.success({
                title: i18n.t('communications.video.statusMessage.successTitle'),
                text: i18n.t('communications.video.statusMessage.successText'),
            });
        });

        addHandler('upload_selected', () => {
            this.updateVideo(id, { source: 'uploaded' });

            // eslint-disable-next-line no-underscore-dangle
            if (!/^video\/\w{2,4}/.test(recorder._videoFile.type)) {
                recorder.reset();
                this.deleteVideo(id);
                behavior.track('VideoPresenting', {
                    action: 'upload',
                    file: 'bad',
                });
                notify.error(i18n.t('communications.video.statusMessage.wrongFileType'));
                return;
            }

            behavior.track('VideoPresenting', {
                action: 'upload',
                file: 'good',
            });
        });
    }

    private disposeRecorderLifecycle(id: number): void {
        const lifecycle = this.recorderLifecycles.get(id);

        if (!lifecycle) {
            return;
        }

        if (typeof lifecycle.recorder.off === 'function') {
            lifecycle.handlers.forEach((handler, event) => {
                lifecycle.recorder.off(event, handler);
            });
        }

        this.recorderLifecycles.delete(id);
    }

    private getLifecycleGeneration(id: number): number {
        return this.lifecycleGenerations.get(id) || 0;
    }

    private isLifecycleGenerationCurrent(id: number, generation: number): boolean {
        return this.getLifecycleGeneration(id) === generation;
    }

    private getDeletionKey(id: number, token: string): string {
        return `${id}:${token}`;
    }

    private videoMatchesToken(id: number, token: string): boolean {
        const video = this.getVideoById(id);

        return !!video && (video.ziggeoId === token || video.processingToken === token);
    }

    private clearPendingDeletions(id: number): void {
        const keyPrefix = `${id}:`;

        this.pendingDeletions.forEach((_deletion, key) => {
            if (key.startsWith(keyPrefix)) {
                this.pendingDeletions.delete(key);
            }
        });
    }

    private removeBeforeUnloadHandler(id: number): void {
        const handler = this.beforeUnloadHandlers.get(id);

        if (!handler) {
            return;
        }

        window.removeEventListener('beforeunload', handler);
        this.beforeUnloadHandlers.delete(id);
    }

    private startProcessing(id: number, token: string, source: VideoSource): void {
        const processingStartedAt = Date.now();

        this.updateVideo(id, {
            status: 'uploaded',
            progress: 100,
            processingStartedAt,
            processingDelayed: false,
            processingToken: token,
            source,
        });
        this.scheduleProcessingTimer(id, token, PROCESSING_DELAY_MS);
    }

    private resumeProcessingTimer(id: number): void {
        const video = this.getVideoById(id);

        if (video?.status !== 'uploaded') {
            return;
        }

        if (
            video.processingDelayed ||
            video.processingStartedAt === null ||
            !video.processingToken
        ) {
            return;
        }

        const elapsed = Date.now() - video.processingStartedAt;
        this.scheduleProcessingTimer(id, video.processingToken, PROCESSING_DELAY_MS - elapsed);
    }

    private scheduleProcessingTimer(id: number, token: string, delay: number): void {
        this.clearProcessingTimer(id);
        const generation = this.getLifecycleGeneration(id);

        const timer = setTimeout(() => {
            this.processingTimers.delete(id);

            if (!this.isLifecycleGenerationCurrent(id, generation)) {
                return;
            }

            const video = this.getVideoById(id);
            if (video?.status !== 'uploaded') {
                return;
            }

            if (
                video.processingDelayed ||
                video.ziggeoId !== token ||
                video.processingToken !== token
            ) {
                return;
            }

            this.updateVideo(id, { processingDelayed: true });
            this.trackProcessingOutcome(
                'processing-delayed',
                video.processingStartedAt,
                token,
                video.source,
                true
            );
            this.fetchVideoState(id, video, false).catch(() => undefined);
        }, Math.max(0, delay));

        this.processingTimers.set(id, timer);
    }

    private completeProcessing(id: number, token: string): boolean {
        const video = this.getVideoById(id);

        if (!token || video?.status !== 'uploaded') {
            return false;
        }

        if (video.processingToken !== token) {
            return false;
        }

        const pendingDeletion = this.pendingDeletions.get(this.getDeletionKey(id, token));

        if (pendingDeletion?.token === token) {
            pendingDeletion.processed = true;
            return false;
        }

        this.clearVideoLifecycle(id);
        this.trackProcessingOutcome(
            video.processingDelayed ? 'processing-recovered' : 'recording-processed',
            video.processingStartedAt,
            token,
            video.source,
            video.processingDelayed
        );
        this.updateVideo(id, {
            status: 'processed',
            processingStartedAt: null,
            processingDelayed: false,
            processingToken: '',
        });

        return true;
    }

    private restoreAfterDeletionFailure(
        id: number,
        deletionKey: string,
        deletionGeneration: number,
        processingContext: ProcessingContext | null,
        beforeUnloadHandler?: (event: BeforeUnloadEvent) => string
    ): void {
        const pendingDeletion = this.pendingDeletions.get(deletionKey);
        this.pendingDeletions.delete(deletionKey);

        if (
            !processingContext?.processingToken ||
            !this.videoMatchesToken(id, processingContext.processingToken) ||
            !this.isLifecycleGenerationCurrent(id, deletionGeneration)
        ) {
            return;
        }

        if (
            pendingDeletion?.processed &&
            pendingDeletion.generation === deletionGeneration &&
            processingContext.processingToken === pendingDeletion.token
        ) {
            if (this.completeProcessing(id, pendingDeletion.token)) {
                this.removeBeforeUnloadHandler(id);
            }

            return;
        }

        if (beforeUnloadHandler) {
            this.beforeUnloadHandlers.set(id, beforeUnloadHandler);
            window.addEventListener('beforeunload', beforeUnloadHandler);
        }

        this.resumeProcessingTimer(id);
    }

    private failProcessing(id: number, token: string): void {
        const video = this.getVideoById(id);

        if (!token || video?.processingToken !== token) {
            return;
        }

        this.clearProcessingTimer(id);

        if (video.processingDelayed) {
            this.trackProcessingOutcome(
                'processing-failed',
                video.processingStartedAt,
                token,
                video.source,
                true
            );
        }

        this.resetVideoZiggeo(id);
    }

    private trackProcessingOutcome(
        action: string,
        processingStartedAt: number | null,
        token: string,
        source: VideoSource | null,
        delayed: boolean
    ): void {
        behavior.track('VideoPresenting', {
            action,
            processingDurationMs:
                processingStartedAt === null ? 0 : Date.now() - processingStartedAt,
            sdkRevision: ZIGGEO_SDK_REVISION,
            mediaTokenFingerprint: fingerprintMediaToken(token),
            source: source || 'unknown',
            delayed,
        });
    }

    async createLandingPage(
        id: number,
        _userId: number,
        { accountSid, leadSid, title }: { accountSid: string; leadSid: string; title: string }
    ) {
        const video = this.getVideoById(id);

        return axios.post(`${process.env.VUE_APP_VIDEO_SERVICE_URL}/api/video`, {
            ziggeo_id: video?.ziggeoId,
            account_sid: accountSid,
            lead_sid: leadSid,
            title,
        });
    }

    async updateZiggeoVideo(ziggeoId: string, payload: Record<string, unknown>): Promise<void> {
        await this.init();

        if (!ziggeoId || !this.ziggeoApp) {
            return;
        }

        await api.videos.update(ziggeoId, payload);
    }
}

const ziggeo = new Ziggeo();

export { Ziggeo, ziggeo };
export { PROCESSING_DELAY_MS };
export type { VideoSource, ZiggeoVideo };

export default {
    install(app: App) {
        app.config.globalProperties.$ziggeo = ziggeo;
    },
};
